import {
    AutocompleteOptionsWithMetadata,
    BaseItem,
} from "@algolia/autocomplete-core";
import classnames from "classnames";
import React, { MouseEvent, useEffect, useRef } from "react";

import { KeyNames } from "@utils/enums";
import { useScrollLock } from "@utils/hooks/useScrollLock";
import { createKeyHandler } from "@utils/keyboard";
import { Layer, setLayer } from "@utils/layer";

import { useGranite } from "@redux/modules/value/translations/translations.provider";

import { Button } from "@components/General/Button/button.component";
import SearchBoxComponent from "@components/Search/SearchAutocomplete/AutocompleteSearchBox/SearchBox/searchBox.component";
import { useAutocompleteApi } from "@components/Search/SearchAutocomplete/AutocompleteSearchBox/useAutocompleteApi.hook";
import { ResultsPanel } from "@components/Search/SearchAutocomplete/ResultsPanel/resultsPanel.container";

import { useSearchIndexContext } from "@context/searchIndex.context";

import { ProductAlgoliaModel } from "lib/@types/service/product/algolia/product.algolia.model";

import "./autocompleteSearchBox.scss";

export type AutocompleteRef = {
    form: HTMLFormElement | null;
    input: HTMLInputElement | null;
    query: string | null;
};

type AutoCompleteSearchBoxProps = {
    searchPath: string;
    autoCompleteProps?: AutocompleteOptionsWithMetadata<BaseItem>;
    initialProducts?: Array<ProductAlgoliaModel>;
};

const AUTOCOMPLETE_SEARCH_BOX_SCROLL_LOCK = "autocomplete-search-box";

const AutocompleteSearchBox = ({
    searchPath,
    autoCompleteProps,
    initialProducts,
}: AutoCompleteSearchBoxProps) => {
    const { t } = useGranite();

    const { productsIndex, contentPagesIndex, querySuggestionsIndex } =
        useSearchIndexContext();

    const { lock, unlock } = useScrollLock(AUTOCOMPLETE_SEARCH_BOX_SCROLL_LOCK);

    const autocompleteRef = useRef<AutocompleteRef>({
        form: null,
        input: null,
        query: null,
    });
    const panelRef = useRef<HTMLDivElement>(null);

    const {
        autocomplete,
        isOpen,
        open,
        close,
        autocompleteActions,
        autocompleteState,
        items,
    } = useAutocompleteApi({
        searchPath: searchPath,
        autoCompleteProps: autoCompleteProps,
        productsIndex: productsIndex,
        contentPagesIndex: contentPagesIndex,
        querySuggestionsIndex: querySuggestionsIndex,
        autocompleteRef: autocompleteRef,
        initialProducts: initialProducts,
    });

    const props = {
        root: autocomplete.getRootProps({}),
        label: autocomplete.getLabelProps({}),
        panel: autocomplete.getPanelProps({}),
    };

    const handleOnKeyDown = createKeyHandler([
        {
            keys: [KeyNames.ESCAPE],
            handler: close,
        },
        {
            keys: [KeyNames.SPACE],
            handler: event => {
                if (isOpen) return;

                event.preventDefault();
                open();
            },
        },
        {
            defaultExcept: [KeyNames.TAB, KeyNames.SHIFT],
            handler: open,
        },
    ]);

    const handleOnOverlayClick = (event: MouseEvent) => {
        if (event.currentTarget !== event.target) return;
        close();
    };

    useEffect(() => {
        if (
            !autocompleteRef.current.form ||
            !autocompleteRef.current.input ||
            !panelRef.current
        ) {
            return;
        }

        const { onTouchStart, onTouchMove, onMouseDown } =
            autocomplete.getEnvironmentProps({
                formElement: autocompleteRef.current.form,
                inputElement: autocompleteRef.current.input,
                panelElement: panelRef.current,
            });

        window.addEventListener("mousedown", onMouseDown);
        window.addEventListener("touchstart", onTouchStart);
        window.addEventListener("touchmove", onTouchMove);

        return () => {
            window.removeEventListener("mousedown", onMouseDown);
            window.removeEventListener("touchstart", onTouchStart);
            window.removeEventListener("touchmove", onTouchMove);
        };
    }, [autocomplete.getEnvironmentProps]);

    useEffect(() => {
        (isOpen ? lock : unlock)();

        const navigationTopBarElement = document.querySelector(
            "section.navigation__top-bar",
        ) as HTMLElement | null;

        if (navigationTopBarElement) {
            setLayer(navigationTopBarElement, Layer.NAVIGATION, isOpen);
        }
    }, [isOpen]);

    return (
        <>
            <div
                {...props.root}
                className={classnames(
                    "autocompleteSearchBox",
                    isOpen && "autocompleteSearchBox--open",
                )}
                aria-label={t("accessibility.searchBox.label")}
                aria-labelledby={undefined}
                onClick={open}
                onKeyDown={handleOnKeyDown}
            >
                <SearchBoxComponent
                    ref={autocompleteRef}
                    isOpen={isOpen}
                    autocomplete={autocomplete}
                    autocompleteState={autocompleteState}
                    open={open}
                    close={close}
                    autocompleteActions={autocompleteActions}
                />

                {isOpen && (
                    <>
                        <Button
                            type={"button"}
                            color={"light-gray"}
                            className={
                                "autocompleteSearchBox__close d-none d-lg-block"
                            }
                            onClick={close}
                            tabIndex={isOpen ? 0 : -1}
                            aria-label={t("accessibility.searchBox.close")}
                        >
                            <i className={"las la-times"}></i>
                        </Button>

                        <ResultsPanel
                            onMouseDown={props.panel.onMouseDown}
                            items={items}
                            autocompleteState={autocompleteState}
                            autocompleteActions={autocompleteActions}
                            autocompleteRef={autocompleteRef}
                        />
                    </>
                )}
            </div>
            {isOpen && (
                <div
                    className={classnames("autocompleteSearchBox__overlay")}
                    onMouseDown={handleOnOverlayClick}
                ></div>
            )}
        </>
    );
};

export default AutocompleteSearchBox;
